import equals from "fast-deep-equal";

const validator = require("validator");

class ValidationHelper {
  static getValidation(fields, validInit = false) {
    const validation = {};
    const fieldSet = new Set();
    validation["_init"] = {};
    for (const field in fields) {
      fieldSet.add(field);
      validation[field] = {
        value: fields[field].defaultValue,
        isValid:
          typeof fields[field].initValid !== "undefined"
            ? fields[field].initValid
            : validInit,
        rule: fields[field].rule,
      };
      validation["_init"][field] = {
        value: fields[field].defaultValue,
      };
    }
    validation["_fields"] = fieldSet;
    validation["_isValid"] = validInit;

    return validation;
  }

  static hasChanged(validation) {
    const newState = {};
    for (const field of Array.from(validation._fields)) {
      newState[field] = {
        value: validation[field].value,
      };
    }
    return !equals(validation._init, newState);
  }

  static update(event, validation) {
    if (!validation._fields.has(event.target.name)) return validation;
    validation[event.target.name]["value"] = event.target.value;
    validation[event.target.name]["isValid"] = validation[
      event.target.name
    ].rule(event.target.value);
    let isValid = true;
    for (const field of Array.from(validation._fields)) {
      if (!validation[field].isValid) {
        isValid = false;
        break;
      }
    }
    validation["_isValid"] = isValid;
    return Object.assign({}, validation);
  }

  static getData(validation) {
    const data = {};
    validation._fields.forEach((field) => {
      data[field] = validation[field].value;
    });
    return data;
  }

  static setData(validation, data) {
    validation._fields.forEach((field) => {
      validation[field]["value"] = data[field];
    });
  }

  /**
   * Checks if valid integer is given
   * @static
   * @param {Integer} value - integer value
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isInt(value) {
    return parseInt(String(value)) === value;
  }

  /**
   * Checks if valid integer is given
   * @static
   * @param {Integer} value - integer value
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isRegEx(value) {
    if (!ValidationHelper.isString(value) || value.length < 1) return false;
    try {
      new RegExp(value);
      return true;
    } catch (err) {}
    return false;
  }

  /**
   * Checks if valid url is given
   * @static
   * @param {Integer} value - integer value
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isUrl(value) {
    return validator.isURL(String(value));
  }

  static isPath(path) {
    const windowsPathRegex = /^[a-zA-Z]:\\(?:[^<>:"/|?*]+\\)*[^<>:"/|?*]*$/;
    const linuxPathRegex = /^\/([^/\0]+\/?)+$/;
    return windowsPathRegex.test(path) || linuxPathRegex.test(path);
  }

  /**
   * Checks if valid url is given
   * @static
   * @param {Integer} value - integer value
   * @return {Boolean} - If true, data is valid otherwise false
   */

  /**
   * Checks if a boolean is given
   * @static
   * @param {Boolean} value - boolean
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isBoolean(value) {
    return typeof value === "boolean";
  }

  /**
   * Checks if a id is a mongodb id
   * @static
   * @param {String} value - id
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isID(value) {
    const mongodbIdRegEx = /^[a-f\d]{24}$/i;
    return mongodbIdRegEx.test(value);
  }

  /**
   * Checks if a id is a mongodb id or undefined
   * @static
   * @param {String} value - id
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isOptionalID(value) {
    if (value === null) return true;
    const mongodbIdRegEx = /^[a-f\d]{24}$/i;
    return mongodbIdRegEx.test(value);
  }

  /**
   * Checks if a value is a string
   * @static
   * @param {String} value - String
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isString(value) {
    return typeof value === "string" || value instanceof String;
  }

  /**
   * Checks if a value is a string
   * @static
   * @param {String} value - String
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isSHA256(value) {
    return (
      ValidationHelper.isString(value) && validator.isHash(value, ["sha256"])
    );
  }

  /**
   * Checks if a value is an class name
   * @static
   * @param {String} value - Class name
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isClassName(value) {
    value = value.trim();
    return (
      ValidationHelper.isString(value) && value.length > 2 && value.length <= 50
    );
  }

  /**
   * Checks if a value is an class name
   * @static
   * @param {String} value - Class name
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isGroupName(value) {
    value = value.trim();
    return (
      ValidationHelper.isString(value) && value.length > 2 && value.length <= 50
    );
  }

  /**
   * Checks if a value is an class name
   * @static
   * @param {String} value - Class name
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isDescription(value) {
    value = value.trim();
    return (
      ValidationHelper.isString(value) &&
      value.length > 2 &&
      value.length <= 200
    );
  }

  /**
   * Checks if a value is an name, length > 2 && <= 50
   * @static
   * @param {String} value - Name
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isName(value) {
    value = value.trim();
    return (
      ValidationHelper.isString(value) && value.length > 2 && value.length <= 50
    );
  }

  /**
   * Checks if correct TPM version string was given
   * @static
   * @param {String} value - TPM version as string
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isTPMVersion(value) {
    return (
      ValidationHelper.isString(value) &&
      (/^[0-9]*.[0-9]*$/.test(value) || value === "none")
    );
  }

  /**
   * Checks if a password fits the password requirements
   * @static
   * @param {String} value - Password
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isPassword(value){
    return ValidationHelper.isString(value)
        && value.length >= 8
        && /[A-Z]/.test(value)
        && /[a-z]/.test(value)
        && /[0-9]/.test(value);
  }

  /**
   * Checks if correct tested OS string was given
   * @static
   * @param {String} value - Tested OS
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isTestedOS(value) {
    return (
      ValidationHelper.isString(value) &&
      /^[a-zA-Z0-9\-]*_[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value)
    );
  }

  /**
   * Checks if a value is an class name
   * @static
   * @param {String} value - Class name
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isExaminationName(value) {
    value = value.trim();
    return (
      ValidationHelper.isString(value) && value.length > 2 && value.length <= 50
    );
  }

  /**
   * Checks if a value is a valid email address
   * @static
   * @param {String} value - Email address
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isEmail(value) {
    return validator.isEmail(String(value));
  }

  /**
   * Checks if a valid date is given
   * @static
   * @param {String} value - Date
   * @return {Boolean} - If true, date is valid otherwise false
   */
  static isFutureDate(value) {
    const d = new Date(Date.parse(value));
    return ValidationHelper.isDate(value) && d.getTime() >= Date.now();
  }

  /**
   * Checks if a valid date is given
   * @static
   * @param {String} value - Date
   * @return {Boolean} - If true, date is valid otherwise false
   */
  static isDate(value) {
    return !isNaN(Date.parse(value));
  }

  /**
   * Checks if a field is defined in an object
   * @static
   * @param {Object} obj - Object to check
   * @param {String} fieldName - Field name
   * @return {Boolean} - If true, date is valid otherwise false
   */
  static isFieldDefined(obj, fieldName) {
    return obj && typeof obj[fieldName] !== "undefined";
  }

  /**
   * Checks if a valid start date
   * @static
   * @param {String} value - Date
   * @return {Boolean} - If true, date is valid otherwise false
   */
  static isStartDate(value, endDate) {
    const parsed = Date.parse(value);
    return (
      ValidationHelper.isDate(value) &&
      parsed >= Date.now() &&
      parsed < Date.parse(endDate)
    );
  }

  /**
   * Checks if a valid end date
   * @static
   * @param {String} value - Date
   * @return {Boolean} - If true, date is valid otherwise false
   */
  static isEndDate(value, startDate) {
    const parsed = Date.parse(value);
    return (
      ValidationHelper.isDate(value) &&
      parsed >= Date.now() &&
      parsed > Date.parse(startDate)
    );
  }

  /**
   * Checks if a valid year number is given (range 1 year in the past up to 2 years in the future
   * @static
   * @param {Integer} value - Year
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isYear(value) {
    const currentYear = new Date().getFullYear();
    return (
      ValidationHelper.isInt(value) &&
      value >= currentYear - 5 &&
      value <= currentYear + 5
    );
  }

  /**
   * Checks if a value is an array of member objects
   * @static
   * @param {Array<Object>} value - Array of member object
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isMemberArray(value) {
    const rules = [
      {
        key: "authenticatorUserId",
        method: ValidationHelper.isString,
      },
      {
        key: "email",
        method: ValidationHelper.isEmail,
      },
      {
        key: "name",
        method: ValidationHelper.isString,
      },
    ];
    return ValidationHelper.isObjectArray(value, rules) && value.length > 0;
  }

  /**
   * Checks if a value is an array of member objects
   * @static
   * @param {Array<Object>} value - Array of member object
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isMemberArrayGroup(value) {
    const rules = [
      {
        key: "userId",
        method: ValidationHelper.isString,
      },
      {
        key: "email",
        method: ValidationHelper.isEmail,
      },
    ];
    return ValidationHelper.isObjectArray(value, rules) && value.length > 0;
  }

  /**
   * Checks if a value is valid remote attestation object
   * @static
   * @param {Object} value - Remote attestation object
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isRemoteAttestation(value) {
    if (typeof value !== "object") return false;
    const fields = [
      "agent_id",
      "ek_cert",
      "pcr_0",
      "pcr_1",
      "pcr_2",
      "pcr_3",
      "pcr_4",
      "pcr_5",
      "pcr_6",
      "pcr_7",
      "event_log_reference",
    ];
    for (let i in fields) {
      if (
        typeof value[fields[i]] === "undefined" ||
        (typeof value[fields[i]] !== "string" &&
          !(value[fields[i]] instanceof String))
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if an array ha valid objects
   * @static
   * @param {Array<Object>} value - Array of object
   * @return {Boolean} - If true, value is an object
   */
  static isObjectArray(array, rules) {
    if (!Array.isArray(array)) return false;
    for (const i in array) {
      for (const s in rules) {
        if (
          typeof array[i][rules[s].key] === "undefined" ||
          !rules[s].method(array[i][rules[s].key])
        ) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Checks if an array ha valid objects
   * @static
   * @param {Array<Object>} value - Array of object
   * @return {Boolean} - If true, value is an object
   */

  static isVmConfig(vmConfig) {
    if (typeof vmConfig !== "object" || vmConfig === null) return false;

    if ("web_url" in vmConfig) {
      if (
        !ValidationHelper.isUrl(vmConfig.web_url) &&
        vmConfig.web_url.length !== 0
      )
        return false;
    }

    if ("startup_applications" in vmConfig) {
      if (!Array.isArray(vmConfig.startup_applications)) {
        return false;
      }
      for (let app of vmConfig.startup_applications) {
        if (typeof app !== "object" || app === null) {
          return false;
        }
        if (
          !("path" in app && "autoStart" in app) ||
          typeof app.path !== "string" ||
          typeof app.autoStart !== "boolean"
        ) {
          return false;
        }
      }
    }
    return true;
  }

  static isConnectionSetting(settings) {
    if (!Array.isArray(settings)) {
      return false;
    }
    if (settings.length === 0) return true;

    for (let obj of settings) {
      if (typeof obj !== "object" || obj === null) {
        return false;
      }
      const keys = Object.keys(obj);
      if (
        keys.length !== 2 ||
        !keys.includes("key") ||
        !keys.includes("value")
      ) {
        return false;
      }
      if (typeof obj.key !== "string" || typeof obj.value !== "string") {
        return false;
      }
    }

    return true;
  }

  /**
   * Checks if a value is valid keylime policy object is given
   * @static
   * @param {Object} value - Keylime policy object
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isKeylimePolicy(value) {
    if (!ValidationHelper.isObject(value)) return false;
    return (
      typeof value["runtime_policy"] !== "undefined" &&
      typeof value["mb_refstate"] !== "undefined" &&
      Object.keys(value).length === 2 &&
      typeof value["mb_refstate"]["grub_files"] !== "undefined" &&
      typeof value["mb_refstate"]["kernel"] !== "undefined" &&
      typeof value["mb_refstate"]["boot"] !== "undefined" &&
      ValidationHelper.isString(value["mb_refstate"]["roothash"]) &&
      ValidationHelper.isObject(value["mb_refstate"]["grub_files"]) &&
      ValidationHelper.isObject(value["mb_refstate"]["kernel"]) &&
      ValidationHelper.isObject(value["mb_refstate"]["boot"]) &&
      value["mb_refstate"]["roothash"].length > 0 &&
      Object.keys(value["mb_refstate"]).length === 4 &&
      Object.keys(value["mb_refstate"]["grub_files"])
        .map(
          (i) =>
            typeof value["mb_refstate"]["grub_files"][i]["sha256"] ===
              "string" &&
            Object.keys(value["mb_refstate"]["grub_files"][i]).length === 1
        )
        .reduce((accu, curr) => accu && curr, true) &&
      Object.keys(value["mb_refstate"]["kernel"])
        .map(
          (i) =>
            typeof value["mb_refstate"]["kernel"][i]["sha256"] === "string" &&
            Object.keys(value["mb_refstate"]["kernel"][i]).length === 1
        )
        .reduce((accu, curr) => accu && curr, true) &&
      Object.keys(value["mb_refstate"]["boot"])
        .map(
          (i) =>
            typeof value["mb_refstate"]["boot"][i]["sha256"] === "string" &&
            Object.keys(value["mb_refstate"]["boot"][i]).length === 1
        )
        .reduce((accu, curr) => accu && curr, true)
    );
  }

  /**
   * Checks if a value is an array of software catalog objects
   * @static
   * @param {Array<Object>} value - Array of software catalog object
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isSoftwareCatalogsArray(value) {
    const rules = [
      {
        key: "_id",
        method: ValidationHelper.isID,
      },
      {
        key: "name",
        method: ValidationHelper.isString,
      },
      {
        key: "description",
        method: ValidationHelper.isNonEmptyExtendedDescription,
      },
      {
        key: "provider",
        method: (value) => ValidationHelper.isIntInRange(value, 0, 1),
      },
      {
        key: "packages",
        method: (value) => Array.isArray(value),
      },
    ];
    return ValidationHelper.isObjectArray(value, rules) && value.length >= 0;
  }

  static isStringArray(array) {
    if (!Array.isArray(array)) return false;
    for (const s of array) {
      if (!ValidationHelper.isString(s)) {
        return false;
      }
    }
    return true;
  }

  static isIDArray(array) {
    if (!Array.isArray(array) || array.length < 1) return false;
    for (const s of array) {
      if (!ValidationHelper.isID(s)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if a value is a valid extended description with language codes
   * @static
   * @param {Object} value - description
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isExtendedDescription(value) {
    for (const idx in ValidationHelper.supportedLanguages) {
      if (
        !ValidationHelper.isString(
          value[ValidationHelper.supportedLanguages[idx].code]
        )
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if a value is a valid extended description with language codes
   * @static
   * @param {Object} value - description
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isNonEmptyExtendedDescription(value) {
    let isNotEmpty = false;
    for (const idx in ValidationHelper.supportedLanguages) {
      if (!isNotEmpty) {
        isNotEmpty = ValidationHelper.isNonEmptyString(
          value[ValidationHelper.supportedLanguages[idx].code]
        );
      }
    }
    return isNotEmpty;
  }

  static getDefaultValueExtendedDescription() {
    const description = {};
    for (const idx in ValidationHelper.supportedLanguages) {
      description[ValidationHelper.supportedLanguages[idx].code] = "";
    }
    return description;
  }

  /**
   * Checks if a value is an int and is in a defined range
   * @static
   * @param {Object} value - description
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isIntInRange(value, min, max) {
    if (!this.isInt(value)) {
      return false;
    }
    return value >= min && value <= max;
  }

  /**
   * Checks if a value is a string, is not empty and doesn't contain spaces only
   * @static
   * @param {String} value - input
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isNonEmptyString(value) {
    return (
      value && ValidationHelper.isString(value) && value.match(/^ *$/) === null
    );
  }

  /**
   * Checks if value is an object and not null
   * @static
   * @param {Object} value - object
   * @return {Boolean} - If true, value is an object
   */
  static isObject(value) {
    return typeof value === "object" && value !== null;
  }

  /**
   * Checks if a valid security level is given, 0 = No isolation, 1 = Isolation without PIN-Code, 2 = Isolation with PIN-Code, 3 = Isolation with Remote Attestation and without PIN-Code, 4 = Isolation with Remote Attestation and PIN-Code
   * @static
   * @param {Integer} value - Security level
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isSecurityLevel(value) {
    return ValidationHelper.isInt(value) && value >= 0 && value <= 6;
  }

  /**
   * Checks if a value is a string is a challenge object
   * @static
   * @param {Object} value - input
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isChallenge(value) {
    return (
      ValidationHelper.isObject(value) &&
      typeof value["id"] !== "undefined" &&
      typeof value["token"] !== "undefined" &&
      ValidationHelper.isID(value["id"]) &&
      ValidationHelper.isString(value["token"])
    );
  }

  /**
   * Checks if a value is a string, is not empty, doesn't contain spaces only, and has a minLength and an optional maxLength
   * @static
   * @param {String} value - description
   * @param {minLength} value - minimum length
   * @param {maxLength} value - maximum length
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isNonEmptyStringOfLength(value, minLength, maxLength) {
    return (
      value &&
      ValidationHelper.isString(value) &&
      value.match(/^ *$/) === null &&
      value.length >= minLength &&
      (maxLength ? value.length <= maxLength : true)
    );
  }

  /**
   * Checks if a value is a valid pin code
   * @static
   * @param {String} value - input
   * @return {Boolean} - If true, data is valid otherwise false
   */
  static isPinCode(value) {
    return value && value.match(/^\d{6}$/);
  }
}

ValidationHelper.supportedLanguages = [
  { code: "de", translations: "Languages.de" },
  { code: "en", translations: "Languages.en" },
  { code: "fr", translations: "Languages.fr" },
  { code: "it", translations: "Languages.it" },
];

export default ValidationHelper;
